import { createContext, useContext, useLayoutEffect, useMemo, useRef, type RefObject } from 'react';

export const focusableSelector = 'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), summary, [tabindex]:not([tabindex="-1"])';

export function isRendered(element: HTMLElement): boolean {
  if (!element.isConnected || element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
  const visibility = getComputedStyle(element).visibility;
  if (visibility === 'hidden' || visibility === 'collapse') return false;
  for (let ancestor: HTMLElement | null = element; ancestor; ancestor = ancestor.parentElement) {
    const style = getComputedStyle(ancestor);
    if (style.display === 'none' || style.contentVisibility === 'hidden') return false;
    if (ancestor instanceof HTMLDetailsElement && !ancestor.open) {
      const summary = Array.from(ancestor.children).find(child => child.tagName === 'SUMMARY');
      if (!summary?.contains(element)) return false;
    }
  }
  // The native check also excludes nodes without a rendered box; DOM-only environments use the semantic checks above.
  return typeof element.checkVisibility !== 'function' || element.checkVisibility({ checkVisibilityCSS: true });
}

export function focusableElements(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(focusableSelector)).filter(element =>
    !element.matches(':disabled, input[type="hidden"]') &&
    !(element.hasAttribute('tabindex') && Number(element.getAttribute('tabindex')) < 0) && isRendered(element));
}

interface ModalScope { parent: ModalScope | null }
export const ModalParentContext = createContext<ModalScope | null>(null);
interface ModalEntry {
  scope: ModalScope; dialog: HTMLDivElement; owner: HTMLElement; order: number; onDismiss: () => void;
  returnFocus: HTMLElement | null; lastFocus?: HTMLElement; zIndex: string; ariaModal: string | null;
}
interface InertState { value: boolean; attribute: string | null }
interface DocumentState {
  body: HTMLElement; overflow: string; overflowPriority: string; returnFocus: HTMLElement | null;
  backgrounds: Map<HTMLElement, InertState>; observer: MutationObserver;
}
const modals = new Map<ModalScope, ModalEntry>();
let sequence = 0;
let activeModal: ModalEntry | undefined;
let documentState: DocumentState | undefined;

function setInert(element: HTMLElement, value: boolean): void {
  element.inert = value;
  element.toggleAttribute('inert', value);
}

/** Ancestors precede descendants even when React runs the child's layout effect first.
 * Independent branches are ordered by their most recent opening, then by the same rule within each branch. */
function orderedModals(): ModalEntry[] {
  const children = new Map<ModalScope | null, ModalEntry[]>();
  for (const entry of modals.values()) {
    let parent = entry.scope.parent;
    while (parent && !modals.has(parent)) parent = parent.parent;
    const siblings = children.get(parent) ?? []; siblings.push(entry); children.set(parent, siblings);
  }
  const recency = new Map<ModalScope, number>();
  const newest = (entry: ModalEntry): number => {
    const cached = recency.get(entry.scope); if (cached !== undefined) return cached;
    const value = Math.max(entry.order, ...(children.get(entry.scope) ?? []).map(newest)); recency.set(entry.scope, value); return value;
  };
  const result: ModalEntry[] = [];
  const visit = (parent: ModalScope | null) => {
    for (const entry of (children.get(parent) ?? []).sort((a, b) => newest(a) - newest(b))) { result.push(entry); visit(entry.scope); }
  };
  visit(null); return result;
}

function focusDialog(entry: ModalEntry, preferred?: HTMLElement | null): void {
  const candidate = preferred && entry.dialog.contains(preferred) && isRendered(preferred) ? preferred
    : entry.lastFocus && isRendered(entry.lastFocus) ? entry.lastFocus : undefined;
  const elements = focusableElements(entry.dialog);
  (candidate ?? elements.find(element => element.hasAttribute('data-autofocus')) ?? elements[0] ?? entry.dialog).focus();
}

function keydown(event: KeyboardEvent): void {
  const entry = activeModal; if (!entry || event.defaultPrevented) return;
  if (event.key === 'Escape') { event.preventDefault(); entry.onDismiss(); return; }
  if (event.key !== 'Tab') return;
  const elements = focusableElements(entry.dialog); const first = elements[0]; const last = elements.at(-1);
  if (!first) { event.preventDefault(); entry.dialog.focus(); }
  else if (event.shiftKey && (document.activeElement === first || !entry.dialog.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
  else if (!event.shiftKey && (document.activeElement === last || !entry.dialog.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
}

function focusin(event: FocusEvent): void {
  const entry = activeModal; if (!entry || !(event.target instanceof HTMLElement)) return;
  if (entry.dialog.contains(event.target)) entry.lastFocus = event.target;
  else focusDialog(entry);
}

function refreshOwnership(preferredFocus?: HTMLElement | null): void {
  const state = documentState; if (!state) return;
  const stack = orderedModals(); const next = stack.at(-1); const previous = activeModal; activeModal = next;
  if (!next) {
    documentState = undefined; state.observer.disconnect();
    document.removeEventListener('keydown', keydown); document.removeEventListener('focusin', focusin);
    for (const [element, original] of state.backgrounds) {
      element.inert = original.value;
      if (original.attribute === null) element.removeAttribute('inert'); else element.setAttribute('inert', original.attribute);
    }
    if (state.overflow) state.body.style.setProperty('overflow', state.overflow, state.overflowPriority);
    else state.body.style.removeProperty('overflow');
    if (state.returnFocus && isRendered(state.returnFocus)) state.returnFocus.focus();
    return;
  }
  for (const child of Array.from(state.body.children)) {
    if (!(child instanceof HTMLElement)) continue;
    if (!state.backgrounds.has(child)) state.backgrounds.set(child, { value: child.inert === true, attribute: child.getAttribute('inert') });
    setInert(child, child !== next.owner);
  }
  state.body.style.setProperty('overflow', 'hidden');
  stack.forEach((entry, index) => {
    entry.owner.style.zIndex = String(1000 + index);
    entry.dialog.setAttribute('aria-modal', entry === next ? 'true' : 'false');
  });
  if (next !== previous) focusDialog(next, preferredFocus);
}

function registerModal(entry: ModalEntry): () => void {
  if (!documentState) {
    const body = document.body;
    documentState = { body, overflow: body.style.getPropertyValue('overflow'), overflowPriority: body.style.getPropertyPriority('overflow'),
      returnFocus: document.activeElement instanceof HTMLElement ? document.activeElement : null,
      backgrounds: new Map(), observer: new MutationObserver(() => refreshOwnership()) };
    documentState.observer.observe(body, { childList: true });
    document.addEventListener('keydown', keydown); document.addEventListener('focusin', focusin);
  }
  modals.set(entry.scope, entry); refreshOwnership();
  return () => {
    modals.delete(entry.scope); entry.owner.style.zIndex = entry.zIndex;
    if (entry.ariaModal === null) entry.dialog.removeAttribute('aria-modal'); else entry.dialog.setAttribute('aria-modal', entry.ariaModal);
    refreshOwnership(entry.returnFocus);
  };
}

/** One document snapshot belongs to the entire stack. Scope follows the React tree across portals. */
export function useModal(open: boolean, ref: RefObject<HTMLDivElement | null>, onDismiss: () => void): ModalScope {
  const parent = useContext(ModalParentContext); const scope = useMemo(() => ({ parent }), [parent]);
  const callback = useRef(onDismiss);
  useLayoutEffect(() => { callback.current = onDismiss; }, [onDismiss]);
  useLayoutEffect(() => {
    if (!open || !ref.current) return;
    const dialog = ref.current; const owner = dialog.closest<HTMLElement>('.hylo-overlay'); if (!owner) return;
    return registerModal({ scope, dialog, owner, order: ++sequence, onDismiss: () => callback.current(),
      returnFocus: document.activeElement instanceof HTMLElement ? document.activeElement : null,
      zIndex: owner.style.zIndex, ariaModal: dialog.getAttribute('aria-modal') });
  }, [open, ref, scope]);
  return scope;
}
